import { useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { IsLoading, useLoading } from "src/common/components/AppState/IsLoading";
import { useAppDispatch } from "src/common/state/hooks";
import { MediaList } from "src/features/gallery/components/MediaList";
import { MediaDetailContainer } from "src/features/gallery/components/MediaDetail";
import {
  getCurrentModeListGallery,
  getCurrentMediaGalleryId,
  setCurrentMediaSidebar,
  fetchFilteredMedias,
  getMediaSearch,
  getFilteredMedias,
  fetchMediaSearchIndex,
  resetMediaSearch,
} from "src/features/gallery/state/gallerySlice";
import { openSlider } from "src/common/state/slice/modal/modalSlice";
import { PaginationControls } from "src/common/components/PaginationControls/PaginationControls";
import { useGalleryPagination } from "src/features/gallery/useGalleryPagination";
import { MediaSearchPanelContainer } from "src/features/gallery/components/MediaSearchPanel";
import { getCurrentProject } from "src/features/projects/state/projectsSlice";

export const PROJECT_MEDIAS_COMPONENT_ID = "projectMediasComponentId";

const MODAL_ID = "media-detail-slideover";

const ProjectMedias = () => {
  const dispatch = useAppDispatch();

  const { page, limit } = useSelector(getMediaSearch);
  const selectedProject = useSelector(getCurrentProject);
  const projectId = selectedProject?.id ? Number(selectedProject.id) : undefined;

  const filteredMedias = useSelector(getFilteredMedias);

  const galleryLoadingState = useLoading(PROJECT_MEDIAS_COMPONENT_ID);
  const isGalleryLoading = galleryLoadingState < 2;

  const currentModeListGallery = useSelector(getCurrentModeListGallery);
  const currentMediaGalleryId = useSelector(getCurrentMediaGalleryId);

  const rawHandlePageChange = useGalleryPagination({
    componentId: PROJECT_MEDIAS_COMPONENT_ID,
    mode: "projectMedias",
  });

  const handlePageChange = useCallback(
    (arg: number | { newPage?: number }) => {
      if (!projectId) return;
      const base = typeof arg === "number" ? { newPage: arg } : arg ?? {};
      rawHandlePageChange({ ...base, projectId });
    },
    [rawHandlePageChange, projectId],
  );

  useEffect(() => {
    if (!projectId) return;
    const start = (page ?? 0) * (limit ?? 20);
    dispatch(
      fetchFilteredMedias({
        start,
        limit,
        projectId,
        componentId: PROJECT_MEDIAS_COMPONENT_ID,
      }),
    );
  }, [dispatch, projectId, page, limit]);

  useEffect(() => {
    dispatch(
      fetchMediaSearchIndex({
        projectId,
      }),
    );
  }, []);

  const openMediaDetail = useCallback(
    (mediaId: number) => {
      dispatch(openSlider(MODAL_ID));
      dispatch(setCurrentMediaSidebar(mediaId));
    },
    [dispatch],
  );

  useEffect(() => {
    return () => {
      dispatch(resetMediaSearch({ resetEverything: true }));
    };
  }, [dispatch]);

  return (
    <>
      <div className="mt-[1rem] ml-[1.5rem]">
        <MediaSearchPanelContainer
          allowedMediaTypeOverride=""
          nodeId={"0"}
          projectId={projectId}
          componentId={PROJECT_MEDIAS_COMPONENT_ID}
          onSearchChanged={(args) => {
            return handlePageChange({ newPage: 0, ...args });
          }}
        />
      </div>
      <IsLoading componentId={PROJECT_MEDIAS_COMPONENT_ID} showSuccess={false}>
        <MediaList
          medias={filteredMedias}
          isModalEdit={false}
          listMode={!!currentModeListGallery}
          openMediaDetail={openMediaDetail}
          currentMedia={currentMediaGalleryId}
          isMediaPickerMode={false}
        />
      </IsLoading>

      <IsLoading componentId={PROJECT_MEDIAS_COMPONENT_ID} showSuccess={false}>
        <div className="h-[4rem] w-full border-t">
          <PaginationControls
            currentPage={page}
            totalPages={1}
            onPageChange={handlePageChange}
            isLoading={isGalleryLoading}
            className="w-[97%] self-center"
            galleryMode
          />
        </div>
      </IsLoading>

      <MediaDetailContainer id={MODAL_ID} large={false} />
    </>
  );
};

export default ProjectMedias;
